import isPlainObject from 'lodash/isPlainObject';
import {
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  PreparationCompleteData,
} from 'views/EMR/components/FillableForm/components/PreparationSteps';
import {
  FillableFormField,
  FormFieldsData,
  WizardStep,
} from 'views/EMR/components/FillableForm/types';

export type SharedPrepValues = PreparationCompleteData;

function isImageDataUri(value: string): boolean {
  return value.startsWith('data:image');
}

export function resolveGlobalPrepDefaults(
  prep: SharedPrepValues | null | undefined,
): Pick<
FormFieldsData,
'signature' | 'signatureDraw' | 'signatureTyped' | 'initial' | 'initialDraw' | 'initialTyped'
> {
  const signature = prep?.signature ?? '';
  const initial = prep?.initial ?? '';

  return {
    signature,
    signatureDraw: isImageDataUri(signature) ? signature : '',
    signatureTyped: isImageDataUri(signature) ? '' : signature,
    initial,
    initialDraw: isImageDataUri(initial) ? initial : '',
    initialTyped: isImageDataUri(initial) ? '' : initial,
  };
}

export function shouldShowPreparationStep(
  hasSignatureField: boolean,
  hasInitialField: boolean,
  sharedPrepValues: SharedPrepValues | null | undefined,
): boolean {
  const signatureNeeded = hasSignatureField && isEmptyString(sharedPrepValues?.signature);
  const initialNeeded = hasInitialField && isEmptyString(sharedPrepValues?.initial);
  return signatureNeeded || initialNeeded;
}

export function getWizardStepAfterLoad(
  hasSignatureField: boolean,
  hasInitialField: boolean,
  sharedPrepValues: SharedPrepValues | null | undefined,
): WizardStep {
  return shouldShowPreparationStep(hasSignatureField, hasInitialField, sharedPrepValues)
    ? 'preparation'
    : 'filling';
}

export function mergeSharedPrepValues(
  previous: SharedPrepValues | null | undefined,
  next: SharedPrepValues,
): SharedPrepValues {
  return {
    signature: !isEmptyString(next.signature) ? next.signature : (previous?.signature ?? ''),
    initial: !isEmptyString(next.initial) ? next.initial : (previous?.initial ?? ''),
  };
}

function getSharedFieldValue(
  fieldType: FillableFormField['type'],
  prep: SharedPrepValues,
): string | null {
  if (fieldType === 'signature') {
    return prep.signature;
  }
  if (fieldType === 'initial') {
    return prep.initial;
  }
  return null;
}

export function applySharedSignatureToFields(
  fields: FillableFormField[],
  prep: SharedPrepValues,
  setValue: UseFormSetValue<FormFieldsData>,
  getValues: UseFormGetValues<FormFieldsData>,
): void {
  fields.forEach((field) => {
    if (!field.id) {
      return;
    }

    const globalValue = getSharedFieldValue(field.type, prep);

    if (globalValue === null || globalValue === '') {
      return;
    }

    const previous = getValues(field.id);
    const fieldValue = isPlainObject(previous)
      ? previous as FillableFormField
      : field;

    setValue(
      field.id,
      { ...fieldValue, value: globalValue },
      { shouldValidate: false },
    );
  });
}
